import { Box, Button, Stack } from '@mui/material'
import { CameraModels } from '@simplisafe/compass-web'
import { FC, ReactNode, useMemo } from 'react'
import OutdoorIcon from '../../../assets/images/outdoor-s2-icon.svg?react'
import OutdoorS2Icon from '../../../assets/images/outdoor-s3-icon.svg?react'
import DoorbellIcon from '../../../assets/images/doorbell-icon.svg?react'
import { BadgeStatus, UNKNOWN_TYPE } from './cameraOverviewEnums'
import { useAuthenticationContext } from '../../../auth/AuthenticationContextProvider'
import { WRITE_MODERATION_EVENTS_SCOPE } from '../../../constants'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { CameraCardStatusBadge } from './CameraCardStatusBadge'
import { lookupCameraDetails } from '../../../utils/cameraUtils'

const cameraIconsByModel: Partial<Record<CameraModels, ReactNode>> = {
  [CameraModels.Shield]: <OutdoorIcon />,
  [CameraModels.Olympus]: <OutdoorS2Icon />,
  [CameraModels.VideoDoorbellPro]: <DoorbellIcon />,
  [CameraModels.Mockingbird]: <DoorbellIcon />
}

interface CameraCardProps {
  cameraName: string
  model: string
  cameraId: string
  status: BadgeStatus
  isActive: boolean
  onCameraAction: () => void
}

export const CameraCard: FC<CameraCardProps> = ({ cameraName: name, model, cameraId, status, isActive, onCameraAction }) => {
  const { toast } = useSnackbar()
  const authContext = useAuthenticationContext()
  const canTakeAction = authContext.has(WRITE_MODERATION_EVENTS_SCOPE) && status !== BadgeStatus.Unenrolled

  const handleCameraAction = () => {
    if (!canTakeAction) {
      toast('Insufficient permissions to take action on cameras.', { variant: 'error' })
      return
    }
    onCameraAction()
  }

  const cameraDetails = useMemo(() => lookupCameraDetails(model), [model])
  const cameraTypeLabel = cameraDetails?.displayName ?? UNKNOWN_TYPE
  const cameraIcon = cameraIconsByModel[model.toUpperCase() as CameraModels] ?? null

  const getCardBackgroundColor = () => {
    return isActive ? 'var(--bg-surface-high)' : 'var(--bg-surface)'
  }

  const getButtonBgColor = () => {
    return isActive || !canTakeAction ? 'var(--button-disabled)' : 'var(--button-primary)'
  }

  const getButtonTextColor = () => {
    return isActive || !canTakeAction ? 'var(--button-video-fg)' : 'var(--button-primary-fg)'
  }

  const getCameraNameTextColor = () => {
    return isActive ? 'var(--text-inverted)' : 'var(--text-primary)'
  }

  const getCameraTypeTextColor = () => {
    return isActive ? 'var(--text-inverted)' : 'var(--text-secondary)'
  }

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyItems: 'center',
        backgroundColor: getCardBackgroundColor(),
        padding: 'var(--spacing-s)',
        border: '1px solid',
        borderRadius: '8px',
        height: '242px',
        width: '360px',
        boxShadow: isActive ? '0px 4px 4px 0px #00000040' : 'none'
      }}
      data-testid="camera-card"
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
        <CameraCardStatusBadge status={status} />
        <Box>{cameraIcon}</Box>
      </Stack>
      <Stack direction="column">
        <Box sx={{ color: getCameraNameTextColor(), fontWeight: 700 }} data-testid="camera-card-camera-name">
          {name}
        </Box>
        <Box sx={{ color: getCameraTypeTextColor(), fontWeight: 500 }} data-testid="camera-card-camera-type">
          {cameraTypeLabel}
        </Box>
        <Box sx={{ color: getCameraTypeTextColor(), fontWeight: 300, fontSize: 'var(--size-2xs)' }} data-testid="camera-card-camera-id">
          {cameraId}
        </Box>
      </Stack>
      <Box
        onClick={handleCameraAction} // onClick is in the box instead of the button to allow toast to send when the button is disabled
        sx={{
          cursor: !canTakeAction ? 'not-allowed' : 'pointer',
          width: '100%',
          height: '3rem',
          marginBlockStart: 'auto'
        }}
      >
        <Button
          variant="contained"
          data-testid="camera-take-action-button"
          disabled={!canTakeAction}
          sx={{
            backgroundColor: getButtonBgColor(),
            color: getButtonTextColor(),
            width: '100%',
            height: '3rem',
            marginBlockStart: 'auto',
            pointerEvents: 'none',
            fontSize: 'var(--size-s)',
            '&.Mui-disabled': {
              backgroundColor: getButtonBgColor(),
              color: getButtonTextColor(),
              fontSize: 'var(--size-s)',
              opacity: 1
            }
          }}
        >
          Take Action
        </Button>
      </Box>
    </Stack>
  )
}
